import React, { useState, ChangeEvent, FormEvent } from "react";
import emailjs from "emailjs-com";
import { storage } from "../firebaseConfig";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const EnquiryForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    orderDescription: "",
    deadline: "",
    agree: false,
    file: null as File | null,
  });

  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      const { checked } = e.target as HTMLInputElement;
      setFormData({
        ...formData,
        [name]: checked,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({
        ...formData,
        file: e.target.files[0],
      });
    }
  };

  const validateForm = (): boolean => {
    const { name, email, phone, service, orderDescription, deadline, agree } = formData;

    // Name validation
    if (!name) {
      toast.error("Please enter your name");
      return false;
    }

    // Email validation
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailPattern.test(email)) {
      toast.error("Please enter a valid email address");
      return false;
    }

    // Phone validation
    const phonePattern = /^[0-9]+$/; // Only allows numbers
    if (!phone || !phonePattern.test(phone)) {
      toast.error("Please enter a valid phone number");
      return false;
    }

    // Service validation
    if (!service) {
      toast.error("Please select a service");
      return false;
    }

    // Order description validation
    if (!orderDescription) {
      toast.error("Please enter an order description");
      return false;
    }

    // Deadline validation
    if (!deadline) {
      toast.error("Please select a deadline");
      return false;
    }

    // Agreement validation
    if (!agree) {
      toast.error("You must accept the terms and conditions");
      return false;
    }

    return true; // If all validations pass
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validate the form before submission
    if (!validateForm()) {
      return; // Stop submission if validation fails
    }

    setIsLoading(true);

    const { name, email, phone, service, orderDescription, deadline, agree, file } = formData;

    let fileURL = "No file attached";
    if (file) {
      const fileRef = ref(storage, `enquiries/${file.name}`);
      await uploadBytes(fileRef, file);
      fileURL = await getDownloadURL(fileRef);
    }

    const templateParams = {
      name,
      email,
      phone,
      service,
      orderDescription,
      deadline,
      agree: agree ? "Yes" : "No",
      file: fileURL,
    };
    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || "";
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "";
    const userId = process.env.REACT_APP_EMAILJS_USER_ID || "";
    emailjs.init(userId);

    emailjs.send(serviceId, templateId, templateParams, userId).then(
      (response) => {
        console.log("SUCCESS!", response.status, response.text);
        toast.success("Enquiry sent successfully!");
        setFormData({
          name: "",
          email: "",
          phone: "",
          service: "",
          orderDescription: "",
          deadline: "",
          agree: false,
          file: null,
        });
      },
      (err) => {
        console.log("FAILED...", err);
        toast.error("Failed to send enquiry. Please try again.");
      }
    ).finally(() => setIsLoading(false));
  };

  return (
    <div>
      <form
        onSubmit={handleSubmit}
        className="max-w-md mx-auto p-4 pt-5 border rounded-lg shadow-md"
        style={{ position: "relative", backgroundColor: "rgba(255,255,255,0.7)" }}
      >
        <div className="form-head">
          <h2 className="text-2xl font-bold text-orange-500 mb-4 text-left ms-2 ps-5 pt-3">
            Enquiry Form
          </h2>
        </div>
        <div className="mb-4">
          <input
            type="text"
            id="name"
            name="name"
            placeholder="Fullname"
            value={formData.name}
            onChange={handleChange}
            className="w-full p-2 font-normal border bg-white rounded-lg"
            style={{ outlineColor: "rgb(231 111 28)", color: "black" }}
            required
          />
        </div>
        <div className="mb-4">
          <input
            type="email"
            id="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            className="w-full p-2 font-normal border bg-white rounded-lg"
            style={{ outlineColor: "rgb(231 111 28)", color: "black" }}
            required
          />
        </div>
        <div className="mb-4">
          <input
            type="tel"
            id="phone"
            name="phone"
            placeholder="Phone no."
            value={formData.phone}
            onChange={handleChange}
            className="w-full p-2 border font-normal bg-white rounded-lg"
            style={{ outlineColor: "rgb(231 111 28)" }}
            required
          />
        </div>
        <div className="mb-4">
          <label htmlFor="service" className="font-normal">Select a service:</label>
          <select
            id="service"
            name="service"
            value={formData.service}
            onChange={handleChange}
            style={{ padding: '8px', borderRadius: '10px', backgroundColor: "rgba(35, 65, 82)", color: 'white', fontSize: "14px", outline: "none" }}
          >
            <option value="">Select a service</option>
            <option value="review_article">Review Article Writing/ Editing</option>
            <option value="research_article">Research Article Writing/ Editing</option>
            <option value="plagiarism_free">Plagiarism Free Writing</option>
            <option value="proofreading">Proofreading</option>
            <option value="projects_development">Projects Development and Implementation</option>
            <option value="content_writing">Content Writing / Editing</option>
            <option value="survey_analysis">Survey / Data Analysis</option>
            <option value="translation">Article Translation</option>
          </select>
        </div>
        <div className="mb-4">
          <textarea
            id="orderDescription"
            name="orderDescription"
            placeholder="Order Description (Write/Attach)"
            value={formData.orderDescription}
            onChange={handleChange}
            className="w-full p-2 font-normal border bg-white rounded-lg"
            style={{ outlineColor: "rgb(231 111 28)" }}
            required
          />
          <input
            type="file"
            id="file"
            name="file"
            onChange={handleFileChange}
            className="font-normal"
          />
        </div>
        <div className="mb-4">
          <label htmlFor="deadline" className="font-normal">Deadline:</label>
          <input
            type="date"
            id="deadline"
            name="deadline"
            value={formData.deadline}
            onChange={handleChange}
            className="w-full p-2 border font-normal bg-white rounded-lg"
            style={{ outlineColor: "rgb(231 111 28)" }}
            required
          />
        </div>
        <div className="mb-4 flex items-center">
          <input
            type="checkbox"
            id="agree"
            name="agree"
            checked={formData.agree}
            onChange={handleChange}
            className="mr-2 bg-white font-normal"
            style={{ outlineColor: "rgb(231 111 28)" }}
            required
          />
          <label htmlFor="agree" className="font-normal">
            I accept the T&C, agree to receive offers & updates
          </label>
        </div>
        <button
          type="submit"
          disabled={isLoading}
          className="w-full p-2 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-700"
        >
          {isLoading ? "Sending..." : "Send Enquiry"}
        </button>
      </form>
      <ToastContainer />
    </div>
  );
};

export default EnquiryForm;
